<template>
  <div class="column">
    <!-- Main Header -->
    <div class="row items-center text-primary q-pa-md" style="min-height: 56px">
      <PInput
        :model-value="searchQuery"
        :placeholder="filterConfig.searchPlaceholder || 'Search'"
        outlined
        dense
        class="col"
        @update:model-value="$emit('update:search-query', $event)"
      >
        <template #prepend>
          <SearchIcon class="icon--xs text-neutral-50" />
        </template>
      </PInput>

      <!-- Filter Toggle Button with Menu -->
      <PBtn
        v-if="showFilterToggle"
        flat
        round
        dense
        size="sm"
        color="primary"
        class="q-ml-sm relative-position"
      >
        <FilterIcon class="icon--sm" />
        <PTooltip>Filters</PTooltip>

        <!-- Active Filter Indicator -->
        <div
          v-if="filterConfig.selected"
          class="absolute bg-primary"
          style="
            width: 8px;
            height: 8px;
            border-radius: 50%;
            top: 4px;
            right: 4px;
          "
        />

        <!-- Filter Menu -->
        <PMenu :menu-items="filterMenuItems" />
      </PBtn>

      <!-- Close Button -->
      <PBtn
        v-if="showCloseButton"
        flat
        round
        dense
        size="sm"
        color="primary"
        @click="$emit('close')"
      >
        <CloseIcon class="icon--sm" />
        <PTooltip>Close</PTooltip>
      </PBtn>
    </div>

    <!-- Filter Section (Collapsible) -->
    <!-- Filters Section -->
    <div
      v-if="internalShowFilters && filterConfig.enabled"
      class="row q-col-gutter-sm"
      :class="filterClass"
    >
      <!-- Built-in Filter Dropdown -->
      <div v-if="filterConfig.options?.length" class="col-12">
        <PSelectInput
          :model-value="filterConfig.selected"
          :options="filterConfig.options"
          :label="filterConfig.label"
          :placeholder="filterConfig.placeholder"
          label-position="side"
          label-classes="text-xxs"
          dense
          outlined
          clearable
          @update:model-value="
            $emit('update:filter-config', { ...filterConfig, selected: $event })
          "
        />
      </div>

      <!-- Custom Filters Slot -->
      <div v-if="$slots.filters" class="col-12">
        <slot name="filters" />
      </div>
    </div>
  </div>
</template>

<script setup>
import SearchIcon from "src/assets/icons/search.svg";
import FilterIcon from "src/assets/icons/filter.svg";
import CloseIcon from "src/assets/icons/close.svg";

const { isFloating } = inject("floating-chat-dialog", {});

const props = defineProps({
  searchQuery: {
    type: String,
    default: "",
  },
  showFilters: {
    type: Boolean,
    default: false,
  },
  showFilterToggle: {
    type: Boolean,
    default: true,
  },
  showCloseButton: {
    type: Boolean,
    default: true,
  },
  // Consolidated configuration for search and filter
  filterConfig: {
    type: Object,
    default: () => ({
      enabled: true,
      searchPlaceholder: "Search",
      options: [],
      selected: null,
      label: "",
      placeholder: "Select option",
    }),
  },
});

const emit = defineEmits([
  "update:search-query",
  "update:show-filters",
  "update:filter-config",
  "close",
]);

const filterClass = computed(() =>
  unref(isFloating) ? "q-px-xs q-pt-sm" : "q-px-md q-pt-sm",
);

// Internal state for filter visibility
const internalShowFilters = ref(props.showFilters);

// Handle filter selection/deselection
const handleFilterSelect = (selectedFilterValue) => {
  // Toggle selection: if same value clicked, deselect it
  const updatedFilterValue =
    props.filterConfig.selected === selectedFilterValue
      ? null
      : selectedFilterValue;

  emit("update:filter-config", {
    ...props.filterConfig,
    selected: updatedFilterValue,
  });
};

// Compute menu items from filter options
const filterMenuItems = computed(() => {
  if (!props.filterConfig.options?.length) {
    return [{ label: "No filters available", onClick: () => {} }];
  }

  return props.filterConfig.options.map((option) => ({
    label: option.label,
    onClick: () => handleFilterSelect(option.value),
  }));
});

// Watch for external changes to showFilters prop
watch(
  () => props.showFilters,
  (updatedShowFiltersValue) => {
    internalShowFilters.value = updatedShowFiltersValue;
  },
);
</script>
